/** Câte ore de soare prinde fiecare fațadă, la solstițiul de iarnă.
 *
 *  CE CERE NORMA. Ordinul MS 119/2014, art. 3: încăperile de locuit trebuie să
 *  aibă minimum o oră și jumătate de însorire la solstițiul de iarnă. E singura
 *  verificare din bilanțul teritorial care nu se poate face din arii — depinde
 *  de unde e parcela pe glob, cum e orientată clădirea și ce umbrește ce.
 *
 *  CE SE VERIFICĂ AICI, și e important să nu se creadă mai mult. Se calculează
 *  însorirea FAȚADELOR PROPUSE, cu umbrirea dintre corpurile propunerii. NU se
 *  verifică vecinii: documentul canonic descrie parcela, nu cvartalul — n-are
 *  nici geometria clădirilor de dincolo de limită, nici înălțimile lor. Deci
 *  rezultatul răspunde la «ce apartamente îmi ies întunecate», nu la «umbresc
 *  pe cineva». A doua întrebare are nevoie de date pe care nimeni nu le-a adus
 *  încă în proiect.
 *
 *  CUM. Ziua se parcurge din cinci în cinci minute. Pentru fiecare pas în care
 *  soarele e deasupra orizontului ȘI în fața fațadei, se trage o rază din
 *  dreptul ferestrei spre soare și se caută un volum care o taie. Ce rămâne e
 *  timpul în care fereastra chiar vede discul.
 *
 *  Punctul de măsură e la 1,50 m de la pardoseală — parapetul obișnuit — pe
 *  fațadă, nu în axul camerei: norma vorbește despre fereastră. */

import { pozitiaSoarelui, vectorSoare, efemeride, type Loc } from "./soare";

/** Un volum care face umbră: un contur la sol, ridicat la `h`. */
export interface Volum {
  /** Inelul amprentei, în cadrul desenului. Deschis sau închis, nu contează. */
  pts: number[][];
  /** Înălțimea, în metri de la cota zero a planului. */
  h: number;
  /** Cine e — codul corpului, ca să se poată numi fațada în interfață. */
  id: string;
}

export interface FatadaStudiu {
  /** Codul corpului pe planșă — «C1». */
  corp: string;
  /** Care latură a amprentei, în ordinea inelului. */
  latura: number;
  /** Încotro privește, în azimut de busolă (0 = nord). */
  azimut: number;
  /** Lungimea laturii, în metri. */
  lungime: number;
  /** Ore de soare la data de referință, în cea mai defavorabilă poziție de pe
   *  latură — nu media: norma se aplică fiecărei încăperi, nu fațadei. */
  ore: number;
  conform: boolean;
}

export interface Insorire {
  fatade: FatadaStudiu[];
  /** Minimul, adică verdictul. `null` când n-a fost nimic de măsurat. */
  minim: number | null;
  neconforme: number;
  /** Data pe care s-a făcut studiul, ca să se poată scrie în panou. */
  data: { an: number; luna: number; zi: number };
}

/** Pragul normei, în ore. */
export const PRAG_INSORIRE = 1.5;

/** Fațadele mai scurte de atât nu sunt fațade de încăpere — un timpan de 1,2 m
 *  între două corpuri alipite n-are ferestre și n-are ce verifica. */
const LATURA_MIN = 2.0;

/** Înălțimea punctului de măsură deasupra cotei zero. */
const COTA_FEREASTRA = 1.5;

/** Pasul de eșantionare a zilei, în minute. Cinci: la un pas de un sfert de oră
 *  o fereastră strâmtă între două corpuri se pierde întreagă, iar diferența
 *  dintre conform și neconform e chiar de ordinul ăsta. */
const PAS_MIN = 5;

const inel = (pts: number[][]): number[][] => {
  const q = (pts ?? []).filter((p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1]));
  if (q.length > 1 && q[0][0] === q[q.length - 1][0] && q[0][1] === q[q.length - 1][1]) {
    return q.slice(0, -1);
  }
  return q;
};

/** Punctul e în poligon? Trasare de rază, cu marginile tratate consecvent. */
function inPoligon(x: number, y: number, ring: number[][]): boolean {
  let c = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
}

/** Raza `o + t·d` taie prisma (contur ridicat la `h`), la un `t` mai mare decât
 *  `eps`?
 *
 *  Se face în doi pași, fiindcă prisma e un produs: felia pe verticală, între
 *  cota zero și `h`, și interiorul conturului pe orizontală. Raza e în volum
 *  exact pe intervalele în care e în amândouă.
 *
 *  Conturul poate fi CONCAV — amprentele în L sunt obișnuite — deci intervalele
 *  de interior nu se pot deduce dintr-o singură pereche intrare/ieșire. Se
 *  adună toate trecerile prin laturi, se sortează, și se întreabă mijlocul
 *  fiecărui interval dacă e înăuntru. */
export function razaTaiePrisma(o: number[], d: number[], ring: number[][], h: number,
                               eps = 0.02): boolean {
  if (ring.length < 3 || !(h > 0)) return false;

  // Felia pe verticală.
  let tzLo = -Infinity, tzHi = Infinity;
  if (Math.abs(d[2]) < 1e-12) {
    if (o[2] < 0 || o[2] > h) return false;
  } else {
    const ta = (0 - o[2]) / d[2];
    const tb = (h - o[2]) / d[2];
    tzLo = Math.min(ta, tb);
    tzHi = Math.max(ta, tb);
    if (tzHi <= eps) return false;
  }

  // Trecerile prin laturi, în plan.
  const taieri: number[] = [];
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const ax = ring[j][0], ay = ring[j][1];
    const bx = ring[i][0], by = ring[i][1];
    const ex = bx - ax, ey = by - ay;
    const den = d[0] * ey - d[1] * ex;
    if (Math.abs(den) < 1e-12) continue;                 // paralelă cu latura
    const t = ((ax - o[0]) * ey - (ay - o[1]) * ex) / den;
    const u = ((ax - o[0]) * d[1] - (ay - o[1]) * d[0]) / den;
    if (u >= 0 && u <= 1) taieri.push(t);
  }
  if (!taieri.length) {
    // Nicio trecere: raza e ori toată înăuntru (verticală, pornită din volum),
    // ori toată afară.
    if (!inPoligon(o[0], o[1], ring)) return false;
    return Math.min(tzHi, Infinity) > eps;
  }
  taieri.sort((a, b) => a - b);

  // Intervalele candidate, inclusiv cele de dinainte de prima trecere și de
  // după ultima.
  const margini = [-Infinity, ...taieri, Infinity];
  for (let i = 0; i < margini.length - 1; i++) {
    const t0 = margini[i], t1 = margini[i + 1];
    const lo = Math.max(t0, tzLo, eps);
    const hi = Math.min(t1, tzHi);
    if (!(hi > lo)) continue;
    const tm = Number.isFinite(lo) && Number.isFinite(hi) ? (lo + hi) / 2
             : (Number.isFinite(lo) ? lo + 1 : hi - 1);
    if (inPoligon(o[0] + d[0] * tm, o[1] + d[1] * tm, ring)) return true;
  }
  return false;
}

/** Azimutul de busolă al unei normale orizontale din cadrul desenului.
 *
 *  Cadrul are +x est și +y SUD (v. `vectorSoare`), deci nordul e −y: azimutul
 *  se ia din `atan2(est, nord) = atan2(nx, −ny)`. */
export function azimutNormalei(nx: number, ny: number): number {
  const a = (Math.atan2(nx, -ny) * 180) / Math.PI;
  return ((a % 360) + 360) % 360;
}

/** Normala exterioară a laturii `j→i`, pentru un inel dat.
 *
 *  Care din cele două perpendiculare e „afară" nu se poate ghici din latură: se
 *  probează un punct împins pe una din ele și se întreabă conturul. */
function normalaExterioara(ring: number[][], j: number, i: number): [number, number] | null {
  const ax = ring[j][0], ay = ring[j][1];
  const bx = ring[i][0], by = ring[i][1];
  const ex = bx - ax, ey = by - ay;
  const l = Math.hypot(ex, ey);
  if (l < 1e-9) return null;
  let nx = ey / l, ny = -ex / l;
  const mx = (ax + bx) / 2, my = (ay + by) / 2;
  if (inPoligon(mx + nx * 0.05, my + ny * 0.05, ring)) { nx = -nx; ny = -ny; }
  return [nx, ny];
}

/** Studiul, pe o zi de referință — implicit solstițiul de iarnă. */
export function studiuInsorire(volume: Volum[], loc: Loc, fus: number, an: number,
                               opts: { luna?: number; zi?: number } = {}): Insorire {
  const luna = opts.luna ?? 12;
  const zi = opts.zi ?? 21;
  const data = { an, luna, zi };

  // Soarele zilei, o dată pentru toate fațadele: de aici în jos se fac doar
  // produse scalare și tăieri de prisme.
  const e = efemeride(an, luna, zi, loc, fus);
  const pasi: [number, number, number][] = [];
  if (e.rasarit != null && e.apus != null) {
    const m0 = Math.floor((e.rasarit * 60) / PAS_MIN) * PAS_MIN;
    const m1 = Math.ceil((e.apus * 60) / PAS_MIN) * PAS_MIN;
    for (let m = m0; m <= m1; m += PAS_MIN) {
      const s = pozitiaSoarelui(an, luna, zi, m / 60, loc, fus);
      if (s.alt > 0) pasi.push(vectorSoare(s.alt, s.az));
    }
  }

  const inele = volume.map((v) => ({ ring: inel(v.pts), h: v.h, id: v.id }));
  const fatade: FatadaStudiu[] = [];

  for (const v of inele) {
    if (v.ring.length < 3) continue;
    for (let i = 0, j = v.ring.length - 1; i < v.ring.length; j = i++) {
      const ax = v.ring[j][0], ay = v.ring[j][1];
      const bx = v.ring[i][0], by = v.ring[i][1];
      const lung = Math.hypot(bx - ax, by - ay);
      if (lung < LATURA_MIN) continue;
      const n = normalaExterioara(v.ring, j, i);
      if (!n) continue;

      // Trei poziții pe latură, iar verdictul e al celei mai defavorabile:
      // norma se aplică fiecărei încăperi, deci o fațadă lungă care e liberă la
      // un capăt și blocată la celălalt nu e conformă pe medie.
      let minMin = Infinity;
      for (const u of [0.25, 0.5, 0.75]) {
        const px = ax + (bx - ax) * u + n[0] * 0.12;
        const py = ay + (by - ay) * u + n[1] * 0.12;
        const o = [px, py, COTA_FEREASTRA];
        let minute = 0;
        for (const L of pasi) {
          if (L[0] * n[0] + L[1] * n[1] <= 0.02) continue;   // soarele e în spate
          let blocat = false;
          for (const w of inele) {
            if (razaTaiePrisma(o, L, w.ring, w.h)) { blocat = true; break; }
          }
          if (!blocat) minute += PAS_MIN;
        }
        if (minute < minMin) minMin = minute;
      }

      const ore = (minMin === Infinity ? 0 : minMin) / 60;
      fatade.push({
        corp: v.id, latura: j, lungime: lung,
        azimut: azimutNormalei(n[0], n[1]),
        ore, conform: ore >= PRAG_INSORIRE,
      });
    }
  }

  const minim = fatade.length ? Math.min(...fatade.map((f) => f.ore)) : null;
  return { fatade, minim, neconforme: fatade.filter((f) => !f.conform).length, data };
}
